import i18next from 'i18next';
import moment from 'moment';
import { Validator, Success, Failure, ValidationError } from '@totalsoft/pure-validations';
import { isObject } from 'utils/functions';

export const noValidation = Validator(() => Success);

export const isTrue = message =>
  Validator(function isTrue(value) {
    if (typeof value !== 'boolean') return Success;
    if (value) return Success;
    return Failure(ValidationError(message));
  });

export const isValidDateFormat = Validator(function validDateFormat(date) {
  if (!date) return Success;
  const allowedDateFormats = ['DD/MM/YYYY', 'DD.MM.YYYY', 'DD-MM-YYYY', 'YYYY-MM-DD'];
  const newDate = moment(date, allowedDateFormats);
  return newDate.isValid() ? Success : Failure(ValidationError(i18next.t('Validations.Generic.InvalidDateFormat')));
});

export const noSpaceAtBeginning = () => {
  var input = /^[^-\s]([a-zA-Z0-9])/;
  return {
    msg: i18next.t('Validations.Generic.NoSpace'),
    func: value => input.test(String(value))
  };
};

export const isCnp = () => {
  return {
    msg: i18next.t('Validations.Generic.CNP'),
    func: p_cnp => {
      if (!p_cnp) return true;
      if (p_cnp.length !== 13) {
        return false;
      }

      // Validate all are numbers
      if (isNaN(Number(p_cnp)) || parseInt(p_cnp, 10) !== Number(p_cnp)) {
        return false;
      }

      // Setup:
      let i = 0,
        year = 0,
        month = 0,
        day = 0,
        hashResult = 0,
        cnp = [],
        hashTable = [2, 7, 9, 1, 4, 6, 3, 5, 8, 2, 7, 9];
      const extraDateValues = '00:00:00 +0000';

      // Calculate condition number
      for (i = 0; i < 13; i++) {
        cnp[i] = parseInt(p_cnp[i], 10);
        if (i < 12) {
          hashResult = hashResult + cnp[i] * hashTable[i];
        }
      }

      hashResult = hashResult % 11;
      if (hashResult === 10) {
        hashResult = 1;
      }

      // Validate birthday
      year = cnp[1] * 10 + cnp[2];
      month = cnp[3] * 10 + cnp[4];
      day = cnp[5] * 10 + cnp[6];

      switch (cnp[0]) {
        case 1:
        case 2:
          year += 1900;
          break;
        case 3:
        case 4:
          year += 1800;
          break;
        case 5:
        case 6:
          year += 2000;
          break;
        case 7:
        case 8:
        case 9:
          year += 2000;
          if (year > new Date().getFullYear() - 18) {
            year -= 100;
          }
          break;
        default: {
          return false;
        }
      }

      if (year < 1800 || year > 2099) {
        return false;
      }
      const birthday = Date.parse(`${year} ${month} ${day} ${extraDateValues}`);
      if (isNaN(birthday)) {
        return false;
      }

      // Validate condition number
      return cnp[12] === hashResult;
    }
  };
};

export const isMandatory = () => {
  return {
    msg: i18next.t('Validations.Generic.Mandatory'),
    func: value => value !== null && value !== undefined && value !== ''
  };
};

export const isInRange = (min, max) => {
  // const minString = typeof min === "function" ? "" : min;
  // const maxString = typeof max === "function" ? "" : max;
  // const string = " (" + minString + " - " + maxString + ")";
  // TODO: make it right

  return {
    msg: i18next.t('Validations.Generic.OutOfRange'),
    func: (value, document) => {
      const left = typeof min === 'function' ? min(document) : min;
      const right = typeof max === 'function' ? max(document) : max;
      return (!value && value !== 0) || (value >= left && value <= right);
    }
  };
};

export const isDerivedInRange = (map, min, max) => {
  return {
    msg: i18next.t('Validations.Generic.OutOfRange'),
    func: (value, document) => {
      const left = typeof min === 'function' ? min(document) : min;
      const right = typeof max === 'function' ? max(document) : max;
      const derivedValue = map(value);
      return (!derivedValue && derivedValue !== 0) || (derivedValue >= left && derivedValue <= right);
    }
  };
};

export const isGreaterThan = min => {
  return {
    msg: i18next.t('Validations.Generic.OutOfRange'),
    func: (value, document) => {
      const left = typeof min === 'function' ? min(document) : min;
      return value > left;
    }
  };
};

export const dateIsGreaterThan = min => {
  return {
    msg: i18next.t('Validations.Generic.OutOfRange'),
    func: (value, document) => {
      const left = typeof min === 'function' ? min(document) : min;
      return left ? moment(value).isSameOrAfter(moment(left)) : true;
    }
  };
};

export const dateIsLessThan = max => {
  return {
    msg: i18next.t('Validations.Generic.OutOfRange'),
    func: (value, document) => {
      const right = typeof max === 'function' ? max(document) : max;
      return right ? moment(value).isSameOrBefore(moment(right)) : true;
    }
  };
};

export const hasLengthLessThan = max => {
  return {
    msg: i18next.t('Validations.Generic.MaxCharacters', { max: max }),
    func: (value, document) => {
      const right = typeof max === 'function' ? max(document) : max;
      return value ? value.length <= right : true;
    }
  };
};

export const isNotEmpty = Validator(function isNotEmpty(value) {
  return value && value.length > 0 ? Success : Failure(ValidationError(i18next.t('Validations.Generic.Empty')));
});

// Should only be used for a property of an object that's part of an array (ex. items(shape({ example: uniqueProperty })))
export const uniqueProperty = message =>
  Validator(function uniqueProperty(value, context) {
    let parentContext = null;
    for (let pathIndex = context.fieldPath.length - 1; pathIndex > 0; pathIndex--) {
      parentContext = (parentContext || context)?.parentContext;
    }
    const parentModel = parentContext?.parentModel;
    if (!parentModel || !Array.isArray(parentModel)) return Success;

    const modelsWithPropertyValue = parentModel.filter(x => {
      let nestedValue = null;
      for (let pathIndex = 1; pathIndex < context.fieldPath.length; pathIndex++) {
        nestedValue = (nestedValue || x)[context.fieldPath[pathIndex]];
      }
      return nestedValue === value;
    });

    if (modelsWithPropertyValue.length <= 1) return Success;
    return Failure(ValidationError(message || i18next.t('Validations.Generic.FieldDuplicated')));
  });

export const uniqueField = Validator(function uniqueProperty(value, context) {
  let parentContext = null;
  let parentModel = null;
  let modelPathIndex = null;
  for (let pathIndex = context.fieldPath.length - 1; pathIndex > 0; pathIndex--) {
    parentContext = (parentContext || context)?.parentContext;
    parentModel = parentContext?.parentModel;
    if (Array.isArray(parentModel)) {
      modelPathIndex = pathIndex;
      break;
    }
  }

  if (!parentModel || !Array.isArray(parentModel)) return Success;

  const modelsWithPropertyValue = parentModel.filter(x => {
    let nestedValue = null;
    for (let pathIndex = modelPathIndex; pathIndex < context.fieldPath.length; pathIndex++) {
      nestedValue = (nestedValue || x)[context.fieldPath[pathIndex]];
    }
    return nestedValue === value;
  });

  if (modelsWithPropertyValue.length <= 1) return Success;
  return Failure(ValidationError(i18next.t('Validations.Generic.FieldDuplicated')));
});

// Should only be used for properties of an object that's part of an array (ex. items(shape({ example: uniqueProperties })))
// DOES NOT WORK FOR NESTED PROPERTIES
export const uniqueProperties = (properties, messageCaption) =>
  Validator(function uniqueProperty(value, context) {
    if (!isObject(value)) return Success;

    const items = context.parentModel;
    if (!Array.isArray(items)) return Success;

    let internalProperties;
    if (typeof properties === 'string') internalProperties = [properties];
    else if (!Array.isArray(properties)) return Success;
    else internalProperties = properties;

    for (let index = 0; index < items.length; index++) {
      const item = items[index];
      if (item === value) continue;
      let differs = false;
      internalProperties.forEach(property => {
        if (!item[property] || item[property] !== value[property]) differs = true;
      });
      if (!differs) {
        return Failure(ValidationError(i18next.t(messageCaption || 'Validations.Generic.FieldsDuplicated')));
      }
    }
    return Success;
  });

export const distinctFromField = (field, fieldCaption) =>
  Validator(function distinctFromField(value, context) {
    const fieldValue = context.parentModel[field];
    if (fieldValue === value)
      return Failure(ValidationError(i18next.t('Validations.Generic.DistinctFromField', { fieldLabel: i18next.t(fieldCaption) })));
    return Success;
  });

function isValidURL(str) {
  const pattern = new RegExp(
    '^(https?:\\/\\/)?' + // protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
      '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
      '(\\#[-a-z\\d_]*)?$',
    'i'
  ); // fragment locator
  return !!pattern.test(str);
}

export const isURL = () => {
  return {
    msg: i18next.t('Validations.Generic.URL'),
    func: value => isValidURL(value)
  };
};

export const foreach = itemValidationRule => (_, items) =>
  items.reduce(
    (accumulator, item, index) => ({
      ...accumulator,
      [index]: typeof itemValidationRule === 'function' ? itemValidationRule(item) : itemValidationRule
    }),
    {}
  );

export const isPositiveOrZero = Validator(x => {
  if (x == null) return Success;

  return x >= 0 ? Success : Failure(ValidationError(i18next.t('Validations.Generic.PositiveOrZero')));
});

export const valueAmong = (values, message) =>
  Validator(value => {
    if (!value || !values || values.length === 0) return Success;

    if (!values.includes(value))
      return Failure(ValidationError(message || i18next.t('Validations.Generic.ValueMustBeAmong', { values: values.join(', ') })));

    return Success;
  });

export const percentage = Validator(value => {
  if (!value || typeof value !== 'number') return Success;
  if (value < 0 || value > 100) return Failure(ValidationError(i18next.t('Validations.Generic.PercentageBetweenZeroHundred')));
  return Success;
});
